import { useState, useRef, useEffect } from "react";
import { buildClasses } from "../utils/buildClasses";
import { keyCodes } from "../utils/KeyCodes";
import { RyeIcon } from "./RyeIcon";
import { RyeButton } from "./RyeButton";
import { RyeShape, RyeVariant } from "./RyeConsts";

export function RyeSearchBar({
  search,
  startingText = "",
  startingExactMatch = false,
  placeholderText = "",
  size = "md",
  height = "auto",
  width = "md",
  focus = false,
  ackFocus = () => {},
  showExactMatchButton = true,
  highlightWhenActive = false,
  variant = "filled",
  shape = "fully-round",
  onArrowDown,
  onArrowUp,
  onEnterOverride,
  onBlur,
  inputRef: externalInputRef,
  connectedBelow = false,
  growOnFocus = false,
  addExtraVerticalPadding = false,
}: {
  search: (searchText: string, exactMatch: boolean) => void;
  startingText?: string;
  startingExactMatch?: boolean;
  placeholderText?: string;
  size?: "sm" | "md" | "lg";
  height?: "auto" | "full";
  width?: "sm" | "md" | "lg" | "xl" | "full";
  focus?: boolean;
  ackFocus?: () => void;
  showExactMatchButton?: boolean;
  highlightWhenActive?: boolean;
  variant?: RyeVariant;
  shape?: RyeShape;
  // Called instead of the no-op default for arrow keys. Useful when an
  // attached menu wants to intercept navigation.
  onArrowDown?: () => void;
  onArrowUp?: () => void;
  // If provided and returns true, replaces the default Enter behavior
  // (which blurs the input and calls search()).
  onEnterOverride?: () => boolean;
  onBlur?: () => void;
  // Optional ref hook so callers can read/focus the input.
  inputRef?: (el: HTMLInputElement | null) => void;
  // When true, the search bar is rendered as if a menu is attached directly
  // below it: bottom corners flat, no bottom border, focus glow disabled.
  // The shape prop is ignored in this mode (always rounded-rect).
  connectedBelow?: boolean;
  // When true, the input transitions to one size taller while focused. Useful
  // when the search bar needs to match the height of an attached menu's rows.
  growOnFocus?: boolean;
  addExtraVerticalPadding?: boolean;
}): JSX.Element {
  const [text, setText] = useState(startingText);
  const [isExactMatch, setIsExactMatch] = useState(startingExactMatch);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const grownHeight = size === "sm" ? "h-9" : size === "md" ? "h-11" : "h-11";

  // Keep the text in sync with any changes to the input text. One far-fetched
  // reason to make sure we do this is in case someone changes the value
  // on a different device.
  useEffect(() => setText(startingText), [startingText]);
  useEffect(() => setIsExactMatch(startingExactMatch), [startingExactMatch]);

  useEffect(() => {
    if (focus) {
      inputRef.current?.scrollIntoView({
        block: "center",
        inline: "center",
        behavior: "smooth",
      });
      inputRef.current?.select(); // this takes care of focusing too!
      ackFocus();
    }
  }, [ackFocus, focus]);

  function handleOnKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.repeat) {
      return;
    }
    switch (e.code) {
      case keyCodes.ESC:
        inputRef.current?.blur();
        break;
      case keyCodes.ENTER:
        if (onEnterOverride && onEnterOverride()) {
          e.preventDefault();
          break;
        }
        inputRef.current?.blur();
        search(text, isExactMatch);
        break;
      case keyCodes.DOWNARROW:
        if (onArrowDown) {
          e.preventDefault();
          onArrowDown();
        }
        break;
      case keyCodes.UPARROW:
        if (onArrowUp) {
          e.preventDefault();
          onArrowUp();
        }
        break;
    }
  }

  function handleInputChanged(e: React.ChangeEvent<HTMLInputElement>) {
    setText(e.target.value);
    search(e.target.value, isExactMatch);
  }

  function handleExactMatchClick() {
    const newExactMatch = !isExactMatch;
    setIsExactMatch(newExactMatch);
    search(text, newExactMatch);
    inputRef.current?.focus();
  }

  function handleClearClick() {
    setText("");
    search("", isExactMatch);
  }

  // Get one size smaller than the current size
  function getSmallerSize(
    currentSize: "sm" | "md" | "lg",
  ): "xs" | "sm" | "md" | "lg" {
    switch (currentSize) {
      case "lg":
        return "md";
      case "md":
        return "sm";
      case "sm":
        return "xs";
      default:
        return "xs";
    }
  }

  const iconPaddingTop = addExtraVerticalPadding
    ? growOnFocus && isFocused
      ? "pt-3"
      : height === "full"
        ? "pt-2"
        : size === "sm"
          ? "pt-1.5"
          : size === "md"
            ? "pt-2.5"
            : "pt-3"
    : "";

  return (
    <div
      className={buildClasses(
        { if: width === "full", then: "w-full" },
        "flex",
        "items-center",
        "relative",
        {
          if: highlightWhenActive && text.length > 0 && !isFocused,
          then: "text-purple",
        },
        // Growing widens by 1rem; shifting left by half keeps it centred,
        // so it springs outward on both sides.
        {
          if: growOnFocus,
          then: buildClasses(
            "transition-transform",
            "duration-300",
            "ease-[cubic-bezier(0.34,1.4,0.64,1)]",
            "motion-reduce:transition-none",
            { if: isFocused && width !== "full", then: "-translate-x-2" },
          ),
        },
      )}
    >
      <RyeIcon
        name="search"
        size="sm"
        className={buildClasses(
          "absolute",
          "left-3",
          iconPaddingTop,
          {
            if: growOnFocus && isFocused,
            then: "motion-safe:animate-pop",
          },
        )}
      />
      <input
        ref={(el) => {
          (
            inputRef as React.MutableRefObject<HTMLInputElement | null>
          ).current = el;
          externalInputRef?.(el);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onBlur?.();
        }}
        placeholder={placeholderText}
        className={buildClasses(
          {
            switch: size,
            cases: new Map([
              ["sm", buildClasses("text-sm")],
              ["md", buildClasses("text-md")],
              ["lg", buildClasses("text-lg")],
            ]),
          },
          {
            switch: height,
            cases: new Map([
              [
                "auto",
                growOnFocus && isFocused
                  ? (addExtraVerticalPadding ? "h-12 pt-3" : grownHeight)
                  : buildClasses({
                      switch: size,
                      cases: new Map([
                        ["sm", buildClasses(addExtraVerticalPadding ? "h-9 pt-1.5" : "h-7")],
                        ["md", buildClasses(addExtraVerticalPadding ? "h-11 pt-2.5" : "h-9")],
                        ["lg", buildClasses(addExtraVerticalPadding ? "h-12 pt-3" : "h-11")],
                      ]),
                    }),
              ],
              ["full", buildClasses("h-full", { if: addExtraVerticalPadding, then: "pt-2" })],
            ]),
          },
          growOnFocus && isFocused
            ? {
                switch: width,
                cases: new Map([
                  ["sm", "w-48"],
                  ["md", "w-60"],
                  ["lg", "w-[19rem]"],
                  ["xl", "w-[25rem]"],
                  ["full", "w-full"],
                ]),
              }
            : {
                switch: width,
                cases: new Map([
                  ["sm", "w-44"],
                  ["md", "w-56"],
                  ["lg", "w-72"],
                  ["xl", "w-96"],
                  ["full", "w-full"],
                ]),
              },
          "pl-10",
          {
            if: showExactMatchButton && text.length > 0,
            then: "pr-20", // Space for both close and exact match buttons
            else: showExactMatchButton
              ? "pr-12" // Space for exact match button only
              : text.length > 0
                ? "pr-12" // Space for close button only
                : "pr-3", // No buttons
          },
          {
            switch: variant,
            cases: new Map([
              [
                "filled",
                buildClasses({
                  if: highlightWhenActive && text.length > 0 && !isFocused,
                  then: buildClasses(
                    "bg-purple-50",
                    "border-transparent",
                    "hover:shadow-sm",
                  ),
                  else: buildClasses(
                    "bg-well",
                    {
                      if: isFocused || connectedBelow,
                      then: "border-tiny",
                      else: "border",
                    },
                    "border-transparent",
                    { if: !isFocused, then: "hover:shadow-sm" },
                  ),
                }),
              ],
              [
                "outlined",
                buildClasses(
                  {
                    if: isFocused || connectedBelow,
                    then: "border-tiny",
                    else: "border",
                  },
                  {
                    if: highlightWhenActive && text.length > 0 && !isFocused,
                    then: buildClasses(
                      "bg-purple-50",
                      "border-transparent",
                      "hover:shadow-sm",
                    ),
                    else: buildClasses(
                      "bg-transparent",
                      "border-on-surface-200",
                      { if: !isFocused, then: "hover:shadow-sm" },
                    ),
                  },
                ),
              ],
            ]),
          },
          // Shape
          {
            if: connectedBelow,
            then: buildClasses(
              {
                switch: shape,
                cases: new Map([
                  [
                    "rounded-rect",
                    buildClasses("rounded-md", "rounded-b-none"),
                  ],
                  [
                    "fully-round",
                    buildClasses(
                      {
                        switch: size,
                        cases: new Map([
                          ["sm", buildClasses("rounded-t-[18px]")],
                          ["md", buildClasses("rounded-t-[22px]")],
                          ["lg", buildClasses("rounded-t-[22px]")],
                        ]),
                      },
                      "rounded-b-none",
                    ),
                  ],
                ]),
              },
              "border-b-0",
            ),
            else: buildClasses({
              switch: shape,
              cases: new Map([
                ["rounded-rect", buildClasses("rounded-md")],
                ["fully-round", buildClasses("rounded-full")],
              ]),
            }),
          },
          {
            if: connectedBelow || isFocused,
            then: "shadow-popover",
          },
          "outline-none",
          "transition-[background-color,border-color,box-shadow,height,width,border-width]",
          // Growing springs open with a slight overshoot, like the main nav's
          // raised tab. Only the size springs: colours would overshoot past
          // their target, flashing the purple active fill to white. The
          // timings follow the transition's property order above.
          {
            if: growOnFocus,
            then: buildClasses(
              "duration-300",
              "[transition-timing-function:ease-out,ease-out,ease-out,cubic-bezier(0.34,1.4,0.64,1),cubic-bezier(0.34,1.4,0.64,1),ease-out]",
              "motion-reduce:transition-none",
            ),
          },
        )}
        value={text}
        onChange={(e) => handleInputChanged(e)}
        onKeyDown={(e) => handleOnKeyDown(e)}
      />
      {/* Close button */}
      {text.length > 0 && (
        <div
          className={buildClasses(
            "flex",
            "items-center",
            "absolute",
            { if: showExactMatchButton, then: "right-9", else: "right-2" },
            "top-1/2",
            "transform",
            "-translate-y-1/2",
          )}
        >
          <RyeButton
            icon="close"
            onClick={handleClearClick}
            variant="transparent"
            vibe={highlightWhenActive ? "primary" : "subdued"}
            size={getSmallerSize(size)}
            shape="fully-round"
          />
        </div>
      )}

      {/* Exact match button */}
      {showExactMatchButton && (
        <div
          className={buildClasses(
            "flex",
            "items-center",
            "absolute",
            "right-2",
            "top-1/2",
            "transform",
            "-translate-y-1/2",
          )}
        >
          <RyeButton
            icon="match_word"
            onClick={handleExactMatchClick}
            variant={isExactMatch ? "filled" : "transparent"}
            vibe={
              isExactMatch
                ? highlightWhenActive && text.length > 0
                  ? "primary"
                  : "ordinary"
                : "subdued"
            }
            size={getSmallerSize(size)}
            iconSize={size}
            shape="rounded-rect"
            tooltip={
              isExactMatch
                ? "Match subsets of text"
                : "Match entire text (do not match subsets of text)"
            }
          />
        </div>
      )}
    </div>
  );
}
